import React, { ChangeEvent, useRef, MutableRefObject, useEffect, useState } from 'react';
import classNames from 'classnames';
import './ComboBox.less';
import './text-inputs.less';
import { DropDownOverlay, IOverlayProps } from './DropDownOverlay';
import { IconButton } from '../buttons/IconButton';
import { ValidationSummary } from 'utils/ValidationUtils';
import { useTrackFocus } from './hooks/useTrackFocus';
import { ValidationWrapper } from './Validation/ValidationWrapper';
import { useTabbingContext } from 'contexts/TabbingContext';
import { mergeRefs } from 'utils/Refs';

export interface IComboBoxControlProps {
  open: boolean;
  onOpenClick: () => void;
  onClose: () => void;
}

export interface IComboBoxProps extends React.HTMLProps<{}> {
  onChange?: React.EventHandler<ChangeEvent<HTMLInputElement>>;
  onClear?: React.MouseEventHandler<HTMLButtonElement>;
  layout?: any; // important post-property grid
  /**
   * true to force it open and ignore internal isOpen state
   */
  open?: boolean;
  /** i18n hook for clear icon button */
  clearTitle?: string;
  /** i18n hook for open icon button */
  openTitle?: string;
  //** watermark */
  placeholder?: string;
  validation?: ValidationSummary;
  hideClear?: boolean;
  /**
   * Provide control props to manage the open/close of this from the outside.
   */
  controlProps?: IComboBoxControlProps;
  openIconClass?: string;
  /**
   * data-testid layer. For testing.
   */
  layerTestId?: string;
  layerProps?: Partial<Omit<IOverlayProps, 'isOpen' | 'onClose' | 'children'>>;
  inputClass?: string;
  iconButtonClass?: string;
  overlayClass?: string;
  fontStyle?: React.CSSProperties;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  useRippleEffect?: boolean;
}

const getClasses = (layout: any, isFocused?: boolean, className?: string) =>
  classNames('dpc-combo-box', 'dpc-text-input-wrapper', className, {
    'dpc-text-input-wrapper--focused': isFocused,
    'dpc-combo-box--focused': isFocused,
    'full-size': layout,
  });

const getInputClassNames = (className?: string) => classNames('dpc-combo-box__input', 'dpc-text-input', className);
// others later if we need them: 'dp-combobox__input--has-clear', 'dp-combobox__input--has-arrow')

/**
 * An un-opinionated Combobox component, to allowing for rendering / filtering different things
 * in different ways in different contexts.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export const ComboBox = React.forwardRef(
  (
    {
      children,
      className,
      clearTitle,
      id,
      layout,
      onChange,
      onClear,
      onFocus,
      onBlur,
      onKeyUp,
      onKeyDown,
      open,
      openIconClass,
      openTitle,
      value,
      placeholder,
      validation,
      hideClear,
      controlProps,
      tabIndex: propsTabIndex,
      autoFocus,
      layerProps,
      layerTestId,
    }: IComboBoxProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const [isOpen, setIsOpen] = useState(false); // looking more like this is just dumb parts, and we'll be controlling this from the outside.
    const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
    const tabIndex = useTabbingContext(propsTabIndex);

    useEffect(() => {
      setIsOpen(!!open);
    }, [open]);

    const toggleOpen = () => {
      setIsOpen(isOpened => !isOpened);
    };

    const onOpen = controlProps ? controlProps.onOpenClick : toggleOpen;
    const onClose = controlProps ? controlProps.onClose : toggleOpen;
    const showDropDown = controlProps ? controlProps.open : isOpen;

    const { isFocused, focusTrackingProps } = useTrackFocus({ onFocus, onBlur });

    return (
      <ValidationWrapper validation={validation} shouldShow={!showDropDown}>
        <div
          className={getClasses(layout, isFocused, className)}
          ref={mergeRefs([wrapperRef, ref])}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
        >
          <input
            autoComplete="off"
            autoFocus={autoFocus}
            id={id}
            type="text"
            className={getInputClassNames()}
            placeholder={placeholder}
            aria-label={placeholder}
            value={value}
            onChange={onChange}
            tabIndex={tabIndex}
            {...focusTrackingProps}
          />
          {!hideClear && value && (
            <IconButton
              className="dpc-combo-box__clear"
              onClick={onClear}
              title={clearTitle || 'Clear'}
              iconClassName="dpc-combo-box__clear-icon"
              iconSize="input"
            />
          )}
          <IconButton
            className="dpc-combo-box__arrow-down"
            onClick={onOpen}
            title={openTitle || 'Open'}
            iconClassName={openIconClass || 'dpc-combo-box__arrow-down-icon'}
            iconSize="input"
          />
          <DropDownOverlay
            sourceRef={wrapperRef}
            isOpen={showDropDown}
            onClose={onClose}
            data-testid={layerTestId}
            {...layerProps}
          >
            {children}
          </DropDownOverlay>
        </div>
      </ValidationWrapper>
    );
  }
);
